import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail, Phone, User, Calendar, Sparkles } from "lucide-react";
import CoursPicker from "../components/CoursPicker";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function CoursEssaiGratuit() {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", program: "essai-double",
    age: "", child_name: "", child_age: "", message: "",
    preferred_date: "", preferred_slot: "",
    source: "site-cours-essai-gratuit",
    utm_medium: null, utm_campaign: null, referrer: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [days, setDays] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offer, setOffer] = useState("cours");
  const [paidLead, setPaidLead] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/essai/offres`).then((r) => setOffers(r.data.offers || [])).catch(() => {});
    const wanted = new URLSearchParams(window.location.search).get("offer");
    if (wanted === "decouverte" || wanted === "cours") setOffer(wanted);
    const sid = new URLSearchParams(window.location.search).get("session_id");
    const cancel = new URLSearchParams(window.location.search).get("cancel");
    if (cancel) toast.info("Paiement annulé — ta réservation n'est pas confirmée. Tu peux réessayer.");
    if (sid) {
      setChecking(true);
      axios.get(`${API}/public/essai/checkout/status`, { params: { session_id: sid } })
        .then((r) => { if (r.data.paid) { setPaidLead(r.data.lead); setSubmitted(true); } else toast.error("Paiement non complété."); })
        .catch(() => toast.error("Impossible de vérifier le paiement. Contacte-nous au (819) 527-9446."))
        .finally(() => setChecking(false));
    }
  }, []);
  const singleOnly = ["kids", "powerkick-her", "pkh", "petits-samourais"].includes(form.program);
  const visibleOffers = singleOnly ? offers.filter((o) => o.id === "cours") : offers;
  const chosen = visibleOffers.find((o) => o.id === offer) || visibleOffers[0];

  const JOURS_FR = { Dimanche: 0, Lundi: 1, Mardi: 2, Mercredi: 3, Jeudi: 4, Vendredi: 5, Samedi: 6 };
  const nextDateForDay = (jour) => {
    const target = JOURS_FR[jour];
    if (target === undefined) return "";
    const d = new Date(); d.setHours(0, 0, 0, 0);
    let add = (target - d.getDay() + 7) % 7;
    if (add === 0) add = 7;
    d.setDate(d.getDate() + add);
    return d.toISOString().slice(0, 10);
  };

  useEffect(() => {
    axios.get(`${API}/schedule/public`).then(r => setDays(r.data?.days || [])).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Cours d'essai 19,98 $ — Institut Impact";
    axios.get(`${API}/leads/programs`).then(r => setPrograms(r.data)).catch(() => {});

    // Capture UTM-like params from URL : ?src=...&utm_medium=...&utm_campaign=...
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src") || params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");
    const referrer = document.referrer || null;
    const pre = { program: params.get("program"), name: params.get("name"), email: params.get("email"), phone: params.get("phone") };
    if (src || utm_medium || utm_campaign || referrer || pre.program || pre.name) {
      setForm((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.entries(pre).filter(([, v]) => v)),
        source: src || prev.source,
        utm_medium,
        utm_campaign,
        referrer,
      }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Nom et courriel requis");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.child_age) payload.child_age = parseInt(payload.child_age);
      else delete payload.child_age;
      payload.offer = chosen?.id || "cours";
      payload.origin_url = window.location.origin;
      const { data } = await axios.post(`${API}/public/essai/checkout`, payload);
      toast.success("Redirection vers le paiement sécurisé…");
      window.location.href = data.url;
      return;
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg = "Une erreur est survenue, réessayez.";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x?.msg || "").filter(Boolean).join(" · ") || msg;
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (checking) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="essai-checking"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /><span className="ml-3">Vérification du paiement…</span></div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white flex items-center justify-center px-6" data-testid="essai-success">
        <div className="max-w-lg text-center">
          <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-4">Paiement reçu — c'est confirmé!</h1>
          {paidLead && (
            <div className="mb-6 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 text-left text-sm" data-testid="essai-paid-summary">
              <div className="font-semibold text-white">{paidLead.trial_offer_label}</div>
              <div className="text-zinc-400">{paidLead.name} · {paidLead.email}</div>
              {paidLead.preferred_slot && <div className="text-zinc-300 mt-1">Cours choisi : {paidLead.preferred_slot}</div>}
              {paidLead.amount_paid ? <div className="text-emerald-300 mt-1">Payé : {Number(paidLead.amount_paid).toFixed(2)} $ (taxes incluses)</div> : null}
            </div>
          )}
          <p className="text-zinc-400 text-lg mb-6">
            Kru Ralph vous contactera <strong className="text-white">dans les 24 heures</strong> pour confirmer la date et l'horaire qui vous conviennent. Un reçu Stripe vous a été envoyé par courriel.
          </p>
          {paidLead?.trial_student_id && (
            <div className="mb-6 p-4 rounded-xl border border-amber-400/40 bg-amber-400/5 text-left" data-testid="essai-etape-integration">
              <div className="text-xs uppercase tracking-widest text-amber-300/80 mb-1">Étape recommandée avant votre cours</div>
              <div className="text-sm text-zinc-300 mb-3">Prenez 3 minutes pour compléter votre étape d'intégration (objectifs, engagement, sécurité). Elle nous aide à recommander le bon groupe et le bon départ.</div>
              <Link to={`/etape-integration/${paidLead.trial_student_id}`} data-testid="essai-etape-link" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm">Compléter mon étape d'intégration →</Link>
            </div>
          )}
          <p className="text-zinc-500 text-sm mb-8">
            Vérifiez votre courriel — une confirmation détaillée vient de partir.
          </p>
          <Link to="/store" className="inline-block px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold">
            Découvrir la boutique →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white" data-testid="essai-page">
      {/* HERO */}
      <section className="border-b border-zinc-800 px-6 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Sans engagement · Places limitées · Paiement sécurisé
          </div>
          <h1 className="text-4xl sm:text-6xl font-bold mb-6 leading-tight">
            Cours d'essai <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">19,98 $</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Découvrez le <strong className="text-white">Kick-Boxing Muay-Thaï</strong>, le <strong className="text-white">Jiu-jitsu brésilien</strong> ou le <strong className="text-white">Jiu-jitsu Can Ryu</strong> à l'Institut Impact de Val-d'Or. Un vrai cours, un vrai coach, un vrai départ. Réservez en ligne, on s'occupe du reste.
          </p>
          <p className="mt-3 text-sm text-amber-200/80">Ou le <strong className="text-amber-300">Forfait Découverte — 2 semaines illimité à 120 $</strong> pour essayer toutes les disciplines.</p>
        </div>
      </section>

      {/* FORMULAIRE */}
      <section className="px-6 py-12">
        <form onSubmit={submit} className="max-w-2xl mx-auto space-y-5 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8" data-testid="essai-form">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-400" /> Réservez votre essai
          </h2>

          <a
            href="/horaire"
            target="_blank"
            rel="noreferrer"
            data-testid="essai-voir-horaire"
            className="flex items-center justify-between gap-3 mb-6 p-4 rounded-lg border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 transition group"
          >
            <span className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-amber-400 shrink-0" />
              <span>
                <span className="block text-sm font-semibold text-white">Consultez l'horaire des cours</span>
                <span className="block text-xs text-zinc-400">Voyez les jours et heures avant de choisir votre essai</span>
              </span>
            </span>
            <span className="text-amber-300 text-sm font-semibold group-hover:translate-x-0.5 transition-transform">Voir l'horaire →</span>
          </a>

          <div>
            <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Programme qui vous intéresse *</label>
            <div className="space-y-2">
              {programs.map(p => (
                <label key={p.id} className={`block p-3 rounded-lg border cursor-pointer transition ${form.program === p.id ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-700 bg-zinc-950 hover:border-zinc-600"}`}>
                  <input type="radio" name="program" value={p.id} checked={form.program === p.id}
                         onChange={e => setForm({ ...form, program: e.target.value })}
                         className="hidden" data-testid={`program-${p.id}`} />
                  <div className="font-semibold text-white text-sm">{p.label}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{p.description}</div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Votre essai *</label>
            <div className={`grid gap-3 ${visibleOffers.length > 1 ? "sm:grid-cols-2" : ""}`} data-testid="essai-offers">
              {visibleOffers.map((o) => (
                <button type="button" key={o.id} onClick={() => setOffer(o.id)} data-testid={`offer-${o.id}`}
                  className={`text-left p-4 rounded-xl border transition ${chosen?.id === o.id ? "border-amber-500 bg-amber-500/10" : "border-zinc-700 bg-zinc-950 hover:border-zinc-500"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-amber-300">{o.badge}</div>
                  <div className="font-bold text-white mt-1">{o.label}</div>
                  <div className="text-2xl font-black text-amber-400 mt-1">{o.price.toFixed(2).replace(".", ",")} $ <span className="text-xs font-normal text-zinc-500">+ taxes{o.id === "decouverte" ? " / 2 sem." : ""}</span></div>
                  <div className="text-xs text-zinc-400 mt-1">{o.desc}</div>
                </button>
              ))}
            </div>
            {singleOnly && <p className="text-xs text-zinc-500 mt-2">Pour ce programme, l'essai se fait sur 1 cours (19,98 $ + taxes).</p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Votre nom complet *</label>
              <div className="relative">
                <User className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <input data-testid="essai-name" type="text" required
                       value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                       className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Courriel *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <input data-testid="essai-email" type="email" required
                       value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                       className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Téléphone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <input data-testid="essai-phone" type="tel" placeholder="819 555-1234"
                       value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                       className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Votre âge (ou adulte)</label>
              <input data-testid="essai-age" type="text" placeholder="ex: 32 ou adulte"
                     value={form.age} onChange={e => setForm({ ...form, age: e.target.value })}
                     className="w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Choisis ton cours d'essai</label>
            {days.length > 0 ? (
              <div className="p-4 rounded-lg border border-zinc-700 bg-zinc-950/60">
                <CoursPicker days={days} value={form.preferred_slot}
                  onChange={(s) => setForm({ ...form, preferred_slot: s ? s.value : "", preferred_date: s ? nextDateForDay(s.jour) : "" })} />
              </div>
            ) : (
              <input data-testid="essai-date" type="date"
                     value={form.preferred_date} onChange={e => setForm({ ...form, preferred_date: e.target.value })}
                     className="w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Comment nous avez-vous connus?</label>
              <select data-testid="essai-source"
                      value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white">
                <option value="site-cours-essai-gratuit">Site web</option>
                <option value="qr">Code QR (sur place / affiche)</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="reference">Référence d'un ami / membre</option>
                <option value="google">Recherche Google</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          {(form.program === "kids" || form.program === "essai-double") && (            <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
              <div className="text-xs font-semibold text-blue-300 mb-3">C'est pour un enfant? Ajoutez les détails :</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="essai-child-name" type="text" placeholder="Nom de l'enfant"
                       value={form.child_name} onChange={e => setForm({ ...form, child_name: e.target.value })}
                       className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
                <input data-testid="essai-child-age" type="number" placeholder="Âge"
                       min="3" max="17"
                       value={form.child_age} onChange={e => setForm({ ...form, child_age: e.target.value })}
                       className="w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-700 text-sm text-white" />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-zinc-400 mb-1.5 block">Message (optionnel)</label>
            <textarea data-testid="essai-message" rows={3}
                      placeholder="Questions, préférences d'horaire, condition particulière..."
                      value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-white" />
          </div>

          <div className="p-3 rounded-lg border border-zinc-700 bg-zinc-950/60 text-xs text-zinc-400" data-testid="essai-fee-notice">
            <strong className="text-zinc-200">Pourquoi un essai payant ?</strong> Parce qu'on investit à 100 % dans chaque personne qui franchit la porte : un coach attentif, un vrai cours, un vrai suivi. Le montant de l'essai n'est pas remboursable ni crédité. Si vous choisissez de vous inscrire ensuite, des <strong className="text-amber-300">frais d'activation K'Ien de 29,95 $ + taxes</strong> s'appliquent une seule fois. Ces frais comprennent la création du dossier membre, l'activation du carnet de progression numérique et l'accès initial à la plateforme. Les frais de programme, les équipements et les taxes applicables sont présentés clairement avant le paiement.
          </div>

          <button
            data-testid="essai-submit"
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-4 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {chosen ? `Payer ${chosen.price.toFixed(2).replace(".", ",")} $ + tx et réserver` : "Réserver mon cours d'essai"}
          </button>

          <p className="text-xs text-zinc-500 text-center">
            Paiement sécurisé par Stripe · Kru Ralph vous contactera dans les 24 heures · Confirmation par courriel
          </p>
        </form>
      </section>

      {/* TRUST FOOTER */}
      <section className="px-6 py-12 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-amber-400 mb-1">25+ ans</div>
            <div className="text-sm text-zinc-400">d'expérience Kru Ralph</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-amber-400 mb-1">220+ techniques</div>
            <div className="text-sm text-zinc-400">curriculum BJJ certifié</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-amber-400 mb-1">Val-d'Or</div>
            <div className="text-sm text-zinc-400">notre dojo professionnel</div>
          </div>
        </div>
      </section>
    </div>
  );
}
